import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, TasksContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Heart, Clock } from 'lucide-react';
import SubscriptionBanner from '@/components/SubscriptionBanner';
import ConflictDialog from '@/components/ConflictDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const pad2 = (n) => String(n).padStart(2, '0');
const toLocalDateStr = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const WEEKDAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const MONTH_RE = MONTHS.join('|');

// "3 pm", "2:30pm", "14:00" -> "HH:MM", or null when no valid time is present
const parseTimeString = (timeStr) => {
  if (!timeStr) return null;
  const cleaned = timeStr.toLowerCase().trim();
  let h, m;
  const ap = cleaned.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/);
  if (ap) {
    h = parseInt(ap[1], 10);
    m = ap[2] ? parseInt(ap[2], 10) : 0;
    if (h < 1 || h > 12) return null;
    if (ap[3] === 'pm' && h !== 12) h += 12;
    if (ap[3] === 'am' && h === 12) h = 0;
  } else {
    const mil = cleaned.match(/\b(\d{1,2}):(\d{2})\b/);
    if (!mil) return null;
    h = parseInt(mil[1], 10);
    m = parseInt(mil[2], 10);
  }
  if (h > 23 || m > 59) return null;
  return `${pad2(h)}:${pad2(m)}`;
};

// "tomorrow 3pm", "today 2:30pm", "jan 30 2pm", "30 jan 9am", "next monday 9am", "fri 14:00"
const parseQuickSchedule = (input) => {
  if (!input) return null;
  const text = input.toLowerCase().trim();
  const time = parseTimeString(text);
  if (!time) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let d = new Date(today);
  const rest = text
    .replace(/(\d{1,2})(?::\d{2})?\s*(am|pm)\b/, ' ')
    .replace(/\b\d{1,2}:\d{2}\b/, ' ');
  if (/\btomorrow\b/.test(rest)) {
    d.setDate(d.getDate() + 1);
  } else if (!/\btoday\b/.test(rest)) {
    const md = rest.match(new RegExp(`\\b(${MONTH_RE})[a-z]*\\.?\\s+(\\d{1,2})\\b`));
    const dm = rest.match(new RegExp(`\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+(${MONTH_RE})[a-z]*\\b`));
    const wd = rest.match(/\b(sun|mon|tue|wed|thu|fri|sat)[a-z]*\b/);
    if (md || dm) {
      const mon = MONTHS.indexOf(md ? md[1] : dm[2]);
      const day = parseInt(md ? md[2] : dm[1], 10);
      d = new Date(now.getFullYear(), mon, day);
      if (d < today) d.setFullYear(d.getFullYear() + 1);
    } else if (wd) {
      let diff = (WEEKDAYS.indexOf(wd[1]) - today.getDay() + 7) % 7;
      if (diff === 0) diff = 7;
      d.setDate(d.getDate() + diff);
    }
  }
  return { date: toLocalDateStr(d), time };
};

const Tasks = () => {
  const { auth } = useContext(AuthContext);
  const { bumpTasks } = useContext(TasksContext);
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    duration: 60,
    priority: 'medium',
    energy_level: 'medium',
    is_flexible: true,
    context_type: 'work',
    quick_schedule: '',
    scheduled_date: toLocalDateStr(new Date()),
    scheduled_time: ''
  });

  const handleCreateTask = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    try {
      let scheduleData = null;
      
      // Check if quick schedule was used
      if (newTask.quick_schedule) {
        const parsed = parseQuickSchedule(newTask.quick_schedule);
        if (parsed) {
          scheduleData = { date: parsed.date, time: parsed.time };
        } else {
          toast.error('Try "tomorrow 3pm", "fri 2:30pm" or "jan 30 9am"');
          setIsCreating(false);
          return;
        }
      }
      // Check if detailed fields were used
      else if (newTask.scheduled_date && newTask.scheduled_time) {
        const parsedTime = parseTimeString(newTask.scheduled_time);
        if (!parsedTime) {
          toast.error('Please enter a valid time (e.g., "3 am" or "2:30 pm")');
          setIsCreating(false);
          return;
        }
        scheduleData = { date: newTask.scheduled_date, time: parsedTime };
      }
      
      // If user specified a schedule - create task with that time
      if (scheduleData) {
        const scheduledDateTime = `${scheduleData.date}T${scheduleData.time}:00`;
        
        const payload = {
          title: newTask.title,
          description: newTask.description,
          duration: Math.max(15, Number(newTask.duration) || 60),
          priority: newTask.priority,
          energy_level: newTask.energy_level,
          is_flexible: newTask.is_flexible,
          context_type: newTask.context_type,
          scheduled_time: new Date(scheduledDateTime).toISOString(),
          status: 'scheduled',
          tz_offset_minutes: -new Date().getTimezoneOffset()
        };
        try {
          await axios.post(`${API}/tasks`, payload, { headers: { Authorization: `Bearer ${auth.token}` } });
        } catch (err) {
          if (err.response?.status === 409) {
            setConflict({ payload, detail: err.response.data.detail });
            setIsCreating(false);
            return;
          }
          throw err;
        }
        bumpTasks(); // signal Dashboard to reload
        toast.success(`Task scheduled for ${scheduleData.date} at ${scheduleData.time}. Check Dashboard!`);
      } else {
        // NO TIME SPECIFIED - Automatically schedule with AI
        toast.info('AI is finding the best time...', { duration: 2000 });
        
        const response = await axios.post(
          `${API}/tasks/schedule`,
          {
            tz_offset_minutes: -new Date().getTimezoneOffset(),
            tasks: [{
              title: newTask.title,
              description: newTask.description,
              duration: Math.max(15, Number(newTask.duration) || 60),
              priority: newTask.priority,
              energy_level: newTask.energy_level,
              is_flexible: newTask.is_flexible,
              context_type: newTask.context_type
            }]
          },
          { headers: { Authorization: `Bearer ${auth.token}` } }
        );
        
        bumpTasks(); // signal Dashboard to reload
        if (response.data.tasks && response.data.tasks.length > 0) {
          const scheduledTask = response.data.tasks[0];
          const scheduledTime = new Date(scheduledTask.scheduled_time).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
          const why = (scheduledTask.reasons || []).join(' · ');
          toast.success(`AI scheduled "${scheduledTask.title}" for ${scheduledTime}`, { description: why || undefined, duration: 7000 });
        } else if (response.data.unscheduled?.length) {
          toast.warning(response.data.message, { duration: 7000 });
        } else {
          toast.success('Task scheduled with AI!');
        }
      }
      
      setNewTask({ 
        title: '', 
        description: '', 
        duration: 60, 
        priority: 'medium',
        energy_level: 'medium',
        is_flexible: true,
        context_type: 'work',
        quick_schedule: '',
        scheduled_date: toLocalDateStr(new Date()),
        scheduled_time: ''
      });
      setIsCreating(false);
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error(error.response.data.detail?.message || 'Free plan limit reached', {
          duration: 8000, action: { label: 'Upgrade', onClick: () => navigate('/settings') },
        });
      } else {
        toast.error('Failed to create task');
      }
      setIsCreating(false);
    }
  };

  const resolveConflict = async (choice) => {
    const { payload, detail } = conflict;
    setConflict(null);
    if (choice === 'cancel') return;
    const body = choice === 'suggested'
      ? { ...payload, scheduled_time: detail.suggested_time }
      : { ...payload, allow_conflicts: true };
    try {
      await axios.post(`${API}/tasks`, body, { headers: { Authorization: `Bearer ${auth.token}` } });
      bumpTasks();
      toast.success(`"${payload.title}" scheduled for ${new Date(body.scheduled_time).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' })}`);
    } catch {
      toast.error('Could not schedule the task');
    }
  };

  return (
    <div className="min-h-screen gradient-bg">
      <ConflictDialog conflict={conflict} onResolve={resolveConflict} />
      <SubscriptionBanner />
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-4xl heading-font font-bold tracking-tight mb-2" data-testid="tasks-title">
            Add Task
          </h1>
          <p className="text-gray-400 body-font">Create a task and AI will find the best time for you</p>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Create Task */}
          <div className="dark-card p-6">
            <div className="flex items-center gap-2 mb-6">
              <Heart className="w-5 h-5 text-teal-400" />
              <h2 className="text-2xl heading-font font-bold" data-testid="create-task-title">New Task</h2>
            </div>
            <form onSubmit={handleCreateTask} className="space-y-4">
              <div>
                <Label htmlFor="title" className="text-gray-300">What needs to be done?</Label>
                <Input
                  id="title"
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  placeholder="Write proposal, Review designs..."
                  required
                  className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-teal-400"
                  data-testid="task-title-input"
                />
              </div>

              <div>
                <Label htmlFor="description" className="text-gray-300">Notes (Optional)</Label>
                <Textarea
                  id="description"
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  placeholder="Any details to remember..."
                  className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-teal-400"
                  data-testid="task-description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="duration" className="text-gray-300">Time needed (min)</Label>
                  <Input
                    id="duration"
                    type="number"
                    value={newTask.duration}
                    onChange={(e) => setNewTask({ ...newTask, duration: parseInt(e.target.value, 10) || '' })}
                    min="15"
                    step="15"
                    className="mt-2 bg-white/5 border-white/10 text-white focus:border-teal-400"
                    data-testid="task-duration-input"
                  />
                </div>

                <div>
                  <Label htmlFor="energy" className="text-gray-300">Mental effort</Label>
                  <Select value={newTask.energy_level} onValueChange={(value) => setNewTask({ ...newTask, energy_level: value })}>
                    <SelectTrigger className="mt-2 bg-white/5 border-white/10 text-white focus:border-teal-400" data-testid="task-energy-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0E27] border-white/10 text-white">
                      <SelectItem value="low" className="text-white hover:bg-white/10">🌱 Low - Easy focus</SelectItem>
                      <SelectItem value="medium" className="text-white hover:bg-white/10">💡 Medium - Normal effort</SelectItem>
                      <SelectItem value="high" className="text-white hover:bg-white/10">⚡ High - Deep focus</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="context" className="text-gray-300">Type</Label>
                  <Select value={newTask.context_type} onValueChange={(value) => setNewTask({ ...newTask, context_type: value })}>
                    <SelectTrigger className="mt-2 bg-white/5 border-white/10 text-white focus:border-teal-400">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0E27] border-white/10 text-white">
                      <SelectItem value="work" className="text-white hover:bg-white/10">Work</SelectItem>
                      <SelectItem value="meeting" className="text-white hover:bg-white/10">Meeting</SelectItem>
                      <SelectItem value="creative" className="text-white hover:bg-white/10">Creative</SelectItem>
                      <SelectItem value="admin" className="text-white hover:bg-white/10">Admin</SelectItem>
                      <SelectItem value="personal" className="text-white hover:bg-white/10">Personal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="priority" className="text-gray-300">Priority</Label>
                  <Select value={newTask.priority} onValueChange={(value) => setNewTask({ ...newTask, priority: value })}>
                    <SelectTrigger className="mt-2 bg-white/5 border-white/10 text-white focus:border-teal-400" data-testid="task-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0E27] border-white/10 text-white">
                      <SelectItem value="low" className="text-white hover:bg-white/10">Low</SelectItem>
                      <SelectItem value="medium" className="text-white hover:bg-white/10">Medium</SelectItem>
                      <SelectItem value="high" className="text-white hover:bg-white/10">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Scheduling Section - Highlighted with Clock Icon */}
              <div className="bg-teal-500/10 border border-teal-400/30 rounded-xl p-4 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Clock size={20} className="text-teal-400" />
                  <p className="text-sm text-teal-400 font-semibold">Schedule Time (Optional)</p>
                </div>
                
                <div>
                  <Label htmlFor="quick_schedule" className="text-gray-300">Type date & time naturally</Label>
                  <Input
                    id="quick_schedule"
                    type="text"
                    value={newTask.quick_schedule}
                    onChange={(e) => setNewTask({ ...newTask, quick_schedule: e.target.value })}
                    placeholder="tomorrow 3pm, today 2:30pm, Jan 30 9am"
                    className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-teal-400"
                    data-testid="quick-schedule-input"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Or use date picker below for specific dates
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label htmlFor="scheduled_date" className="text-gray-300">Date</Label>
                    <Input
                      id="scheduled_date"
                      type="date"
                      value={newTask.scheduled_date}
                      onChange={(e) => setNewTask({ ...newTask, scheduled_date: e.target.value })}
                      className="mt-2 bg-white/5 border-white/10 text-white focus:border-teal-400"
                      data-testid="task-date-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="scheduled_time" className="text-gray-300 flex items-center gap-2">
                      <Clock size={16} className="text-teal-400" />
                      Time (Optional)
                    </Label>
                    <Input
                      id="scheduled_time"
                      type="text"
                      value={newTask.scheduled_time}
                      onChange={(e) => setNewTask({ ...newTask, scheduled_time: e.target.value })}
                      placeholder="Just type: 3 am, 2:30 pm, 9am"
                      className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-teal-400"
                      data-testid="task-time-input"
                    />
                  </div>
                </div>
                
                <p className="text-xs text-gray-500 mt-2">
                  💡 Leave empty and AI will automatically find the best time for you
                </p>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="flexible"
                  checked={newTask.is_flexible}
                  onChange={(e) => setNewTask({ ...newTask, is_flexible: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="flexible" className="text-sm text-gray-400 cursor-pointer">
                  This task is flexible (can be rescheduled if needed)
                </Label>
              </div>

              <Button 
                type="submit" 
                className="w-full teal-btn" 
                data-testid="create-task-btn"
                disabled={isCreating || !newTask.title.trim()}
              >
                {isCreating ? (
                  <>
                    <Clock size={20} className="mr-2 animate-spin" />
                    AI is scheduling...
                  </>
                ) : (
                  <>
                    <Plus size={20} className="mr-2" />
                    Add Task
                  </>
                )}
              </Button>
              
              <p className="text-xs text-center text-gray-500 mt-2">
                Task will be added to your Dashboard calendar
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tasks;